import { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import parseDaytime from '../../util/parse-daytime';

class MessageListener extends Component {
  componentDidMount() {
    document.addEventListener('message', this.handleMessage);
    window.eventFromApp = this.handleMessage;
    this.waitForBridge();
  }

  componentWillUnmount() {
    document.removeEventListener('message', this.handleMessage);
  }

  handleMessage = (event) => {
    const {
      selectedLocation,
      selectedReport,
      comparisonOptions,
      daytimeOptions,
      openNoteDialog,
      loginSucceeded,
      logout,
      navigate,
      selectLocation,
      selectReport,
      setDateRange,
      setComparisonDateRange,
      setFilterOption,
      removeComparisonDateRange,
      removeReportData,
      loadAllReportData,
      loadLocations,
      debug
    } = this.props;

    const eventJSON = JSON.parse(event.data);
    const eventName = eventJSON.name;
    const eventPayload = eventJSON.payload;

    this.postMessageForApp('echo', eventJSON);

    switch (eventName) {
      case 'login':
        loginSucceeded({
          user: eventPayload.userName,
          accessToken: eventPayload.accessToken,
          roles: eventPayload.roles,
          expiresIn: eventPayload.expiresIn,
          refreshToken: eventPayload.refreshToken,
        });
        navigate({ route: '/' });
        if (!selectedLocation) {
          loadLocations();
        }
        break;
      case 'logout':
        logout();
        try {
          window.localStorage.clear();
          window.sessionStorage.clear();
        } catch (error) {
          //
        }

        break;
      case 'loadReport': {
        if (!selectedLocation) {
          loadLocations();
        }
        const { locationId, reportId } = eventPayload;
        selectLocation(locationId);
        if (!selectedReport || reportId !== selectedReport.reportID) {
          removeReportData(0);
          selectReport(reportId);
        }
        window.scrollTo(0, 0);
        break;
      }
      case 'openNoteDialog': {
        openNoteDialog();
        window.scrollTo(0, 0);
        break;
      }
      case 'loadTimeframe': {
        const startDate = moment(eventPayload.startDate).unix();
        const endDate = moment(eventPayload.endDate).unix();
        setDateRange({ startDate, endDate });
        break;
      }
      case 'setFilters': {
        const selectedOption = comparisonOptions[eventPayload.comparison];
        if (selectedOption.pickerVisible) {
          setComparisonDateRange({
            comparisonId: 1,
            startDate: selectedOption.startDate,
            endDate: selectedOption.endDate,
            locationId: selectedLocation.id,
            option: selectedOption.name,
          });
        } else {
          removeComparisonDateRange(1);
          removeReportData(1);
        }

        const weekdays = eventPayload.weekDays;
        setFilterOption({ key: 'daysOfWeek', value: weekdays ? weekdays.map(weekday => weekday + 1) : null });

        const selectedDaytimeIndex = eventPayload.dayTime;
        if (selectedDaytimeIndex === 0 || selectedDaytimeIndex > daytimeOptions.length) {
          setFilterOption({ key: 'startTime', value: null });
          setFilterOption({ key: 'endTime', value: null });
        } else {
          const selectedDaytime = daytimeOptions[selectedDaytimeIndex - 1]; // compensate for added entry
          setFilterOption({ key: 'startTime', value: parseDaytime(selectedDaytime.startTime, true) });
          setFilterOption({ key: 'endTime', value: parseDaytime(selectedDaytime.endTime, true) });
        }
        break;
      }
      case 'requestDayTimeFilterOptions': {
        // add one entry for empty
        const optionsDayTimeFilter = [{ title: 'Keine' }].concat(daytimeOptions.map(daytime => ({
          title: daytime.name,
          staticDayTimeFilter: { startTime: parseDaytime(daytime.startTime), endTime: parseDaytime(daytime.endTime) },
        })));
        this.postMessageForApp('getDayTimeFilterOptions', optionsDayTimeFilter);
        break;
      }
      case 'requestComparisonOptions': {

        const optionsComparisonFilter = comparisonOptions
          .filter(comparisonOption => !comparisonOption.skipSendInMessage)
          .map(comparisonOption => ({
            title: comparisonOption.name,
            key: comparisonOption.name,
          }));
        this.postMessageForApp('getComparisonOptions', optionsComparisonFilter);
        break;
      }
      case 'loadTimeframeForComparison': {
        const startDate = moment(eventPayload.startDate).unix();
        const endDate = moment(eventPayload.endDate).unix();
        setComparisonDateRange({
          comparisonId: 1,
          startDate,
          endDate,
          locationId: selectedLocation.id,
        });
        break;
      }
      case 'refresh':
        if (!selectedLocation) {
          loadLocations();
        }
        loadAllReportData();
        break;
      default:
        break;
    }
  }

  postMessageForApp(name, payload) {
    if (!window.reactNativeBridgeReady) {
      return;
    }

    if (payload === undefined) {
      payload = {};
    }

    const message = {
      name,
      payload,
    };

    const serializedMessage = JSON.stringify(message);

    try {
      window.postMessage(serializedMessage, undefined);
    } catch (error) {
      // failed to send message to old reports apps
    }

    try {
      window.ReactNativeWebView.postMessage(serializedMessage);
    } catch (error) {
      // failed to send message to new reports apps
    }
  }

  waitForBridge() {
    if (window.postMessage.length !== 1 && !window.ReactNativeWebView) {
      setTimeout(this.waitForBridge, 50);
    } else {
      window.reactNativeBridgeReady = true;

      setTimeout(() => {
        this.postMessageForApp('init', undefined);
      }, 500);
      setTimeout(() => {
        this.postMessageForApp('reportsReady', undefined);
      }, 500);
    }
  }

  render() {
    return null;
  }
}

MessageListener.propTypes = {
  selectedLocation: PropTypes.object,
  comparisonOptions: PropTypes.array,
  daytimeOptions: PropTypes.array,
  loginSucceeded: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  selectLocation: PropTypes.func.isRequired,
  selectReport: PropTypes.func.isRequired,
  setDateRange: PropTypes.func.isRequired,
  setComparisonDateRange: PropTypes.func,
  removeComparisonDateRange: PropTypes.func,
  removeReportData: PropTypes.func,
  setFilterOption: PropTypes.func,
  openNoteDialog: PropTypes.func,
  loadAllReportData: PropTypes.func.isRequired,
};

MessageListener.defaultProps = {
  selectedLocation: null,
  comparisonOptions: [],
  daytimeOptions: [],
  setComparisonDateRange: () => {},
  removeComparisonDateRange: () => {},
  removeReportData: () => {},
  setFilterOption: () => {},
  openNoteDialog: () => {},
};

export default MessageListener;
